import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from './LoadingSpinner';

interface TermsApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => Promise<void>;
  isLoading?: boolean;
}

const TermsApprovalModal: React.FC<TermsApprovalModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [hasAccepted, setHasAccepted] = useState(false);

  if (!isOpen) return null;

  const handleAccept = async () => {
    if (!hasAccepted) return;
    await onAccept();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
        
        {/* Modal */}
        <div className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {t('auth.termsApprovalRequired', 'Terms Approval Required')}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
              disabled={isLoading}
              aria-label={t('common.close')}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            <div className="mb-6">
              <p className="text-gray-700 mb-6">
                {t('auth.termsApprovalMessage', 'To continue using our services, you must accept our Terms of Service and Privacy Policy.')}
              </p>
              
              {/* Acceptance Checkbox */}
              <div className="flex items-start">
                <input
                  id="acceptTermsModal"
                  type="checkbox"
                  checked={hasAccepted}
                  onChange={(e) => setHasAccepted(e.target.checked)}
                  className="mt-1 h-5 w-5 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                  disabled={isLoading}
                />
                <label htmlFor="acceptTermsModal" className="ml-3 text-sm text-gray-700">
                  {t('auth.acceptTermsModal', 'I have read and accept the')}{' '}
                  <a 
                    href="/terms" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-cyan-600 hover:text-cyan-500 underline"
                  >
                    {t('navigation.terms', 'Terms of Service')}
                  </a>{' '}
                  {t('common.and', 'and')}{' '}
                  <a 
                    href="/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-cyan-600 hover:text-cyan-500 underline"
                  >
                    {t('navigation.privacy', 'Privacy Policy')}
                  </a>
                </label>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 min-h-[48px] transition-all"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={handleAccept}
              disabled={!hasAccepted || isLoading}
              className="px-4 py-3 text-base font-medium text-white bg-cyan-600 border border-transparent rounded-lg hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center min-h-[48px] transition-all"
            >
              {isLoading && <LoadingSpinner size="sm" className="mr-2" />}
              {t('auth.acceptAndContinue', 'Accept and Continue')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsApprovalModal;